// Frontend subscription service for API calls
import { auth } from '../config/firebase';

const API_BASE_URL = 'https://us-central1-mattressai2.cloudfunctions.net/subscription';

export interface SubscriptionResponse {
  id?: string;
  merchantId?: string;
  plan?: string;
  status?: string;
  billingCycle?: string;
  managementLevel?: string;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  trialEnd?: string;
  cancelAtPeriodEnd?: boolean;
  usage?: {
    start: string;
    end: string;
    chatsUsed: number;
    teamMembersUsed: number;
    integrationsUsed: number;
    storageUsedGB: number;
    locationsUsed: number;
  };
  limits?: {
    maxChatsPerMonth: number;
    maxTeamMembers: number;
    maxIntegrations: number;
    maxStorageGB: number;
    maxLocations: number;
  };
  subscription?: any;
  message?: string;
}

export interface UsageResponse {
  usage: {
    start: string;
    end: string;
    chatsUsed: number;
    teamMembersUsed: number;
    integrationsUsed: number;
    storageUsedGB: number;
    locationsUsed: number;
  };
  limits: {
    maxChatsPerMonth: number;
    maxTeamMembers: number;
    maxIntegrations: number;
    maxStorageGB: number;
    maxLocations: number;
  };
  plan: string;
  managementLevel: string;
  monthlyUsage: Array<{
    month: string;
    chatsUsed: number;
    teamMembersUsed: number;
    integrationsUsed: number;
    storageUsedGB: number;
    locationsUsed: number;
  }>;
}

export interface CheckoutRequest {
  planId: string;
  billingCycle: 'monthly' | 'yearly';
  successUrl?: string;
  cancelUrl?: string;
  discountCode?: string;
}

export interface CheckoutResponse {
  url: string;
}

export interface PortalResponse {
  url: string;
}

// Legacy types for compatibility
interface SubscriptionData {
  id: string;
  merchantId: string;
  plan: string;
  status: string;
  billingCycle: string;
  managementLevel: string;
  currentPeriodStart: Date;
  currentPeriodEnd: Date;
  trialEnd?: Date;
  cancelAtPeriodEnd: boolean;
  createdAt: Date;
  updatedAt: Date;
}

interface UsageData {
  id: string;
  merchantId: string;
  subscriptionId: string;
  currentPeriod: {
    start: Date;
    end: Date;
    chatsUsed: number;
    teamMembersUsed: number;
    integrationsUsed: number;
    storageUsedGB: number;
    locationsUsed: number;
  };
  monthlyUsage: Array<{
    month: string;
    chatsUsed: number;
    teamMembersUsed: number;
    integrationsUsed: number;
    storageUsedGB: number;
    locationsUsed: number;
  }>;
  limits: {
    maxChatsPerMonth: number;
    maxTeamMembers: number;
    maxIntegrations: number;
    maxStorageGB: number;
    maxLocations: number;
  };
  updatedAt: Date;
}

export class SubscriptionService {
  private useMockData = false; // Production: using live backend

  private async getAuthToken(): Promise<string> {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }
    return await user.getIdToken();
  }

  private async makeAuthenticatedRequest<T>(
    endpoint: string, 
    options: RequestInit = {},
    retries: number = 2
  ): Promise<T> {
    const token = await this.getAuthToken();
    
    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
          ...options,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
          },
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          
          // Don't retry for client errors (4xx), only server errors (5xx) and network issues
          if (response.status < 500 || attempt === retries) {
            // Provide user-friendly error messages
            let userMessage = 'Something went wrong. Please try again.';
            
            if (response.status === 401) {
              userMessage = 'Your session has expired. Please sign in again.';
            } else if (response.status === 403) {
              userMessage = 'You don\'t have permission to perform this action.';
            } else if (response.status === 404) {
              userMessage = 'The requested resource was not found.';
            } else if (response.status === 429) {
              userMessage = 'Too many requests. Please wait a moment and try again.';
            } else if (response.status >= 500) {
              userMessage = 'Our servers are experiencing issues. Please try again in a few minutes.';
            } else if (errorData.error) {
              userMessage = errorData.error;
            }
            
            throw new Error(userMessage);
          }
          
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          continue;
        }

        return await response.json();
      } catch (error) {
        // If it's a network error and we have retries left, try again
        if (attempt < retries && (error instanceof TypeError || (error instanceof Error && error.message.includes('fetch')))) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
          continue;
        }
        throw error;
      }
    }
    
    throw new Error('Maximum retries exceeded');
  }

  /**
   * Get current subscription status for the authenticated user
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse> {
    if (this.useMockData) {
      // Return mock data for testing
      return {
        message: 'No subscription found',
        subscription: null
      };
    }

    try {
      return await this.makeAuthenticatedRequest<SubscriptionResponse>('/current');
    } catch (error) {
      console.error('Error getting current subscription:', error);
      
      // Return mock data as fallback for development
      return {
        message: 'No subscription found',
        subscription: null
      };
    }
  }

  /**
   * Check if user has an active subscription
   */
  async hasActiveSubscription(): Promise<boolean> {
    try {
      const response = await this.getCurrentSubscription();
      return !!(response.id && response.status && ['active', 'trialing'].includes(response.status));
    } catch (error) {
      console.error('Error checking subscription status:', error);
      return false;
    }
  }

  /**
   * Get subscription status for login flow
   */
  async getSubscriptionStatus(): Promise<{
    hasSubscription: boolean;
    isActive: boolean;
    plan?: string;
    status?: string;
    trialEnd?: string;
    needsPayment?: boolean;
    isNewProspect?: boolean;
    hasSubscriptionHistory?: boolean;
  }> {
    try {
      const response = await this.getCurrentSubscription();
      
      // Debug logging to understand what backend returns
      console.log('🔍 Subscription response:', {
        id: response.id,
        plan: response.plan,
        status: response.status,
        message: response.message,
        hasSubscription: !!response.subscription,
        fullResponse: response
      });
      
      // Also log the current user for debugging
      const user = auth.currentUser;
      console.log('🔍 Current user:', {
        uid: user?.uid,
        email: user?.email,
        displayName: user?.displayName
      });
      
      // Check if user has an active subscription first (regardless of id field)
      const isActive = ['active', 'trialing'].includes(response.status || '');
      const needsPayment = ['past_due', 'unpaid', 'incomplete'].includes(response.status || '');
      
      // Special handling for Free plan users
      const isFreeUser = response.plan === 'free' && response.status === 'active';
      
      // If user has an active subscription (including free users), they definitely have subscription history
      if (isActive || isFreeUser) {
        return {
          hasSubscription: true,
          isActive: true,
          plan: response.plan,
          status: response.status,
          trialEnd: response.trialEnd,
          needsPayment: false,
          isNewProspect: false,
          hasSubscriptionHistory: true
        };
      }
      
      // If user needs payment but has subscription data, they're existing customer
      if (needsPayment && (response.plan || response.status || response.id)) {
        return {
          hasSubscription: true,
          isActive: false,
          plan: response.plan,
          status: response.status,
          trialEnd: response.trialEnd,
          needsPayment: true,
          isNewProspect: false,
          hasSubscriptionHistory: true
        };
      }
      
      // Check if this user has ever had a subscription by looking for any subscription data
      const hasSubscriptionHistory = !!(response.plan || response.status || response.id);
      
      // Note: Temporary fix removed - all existing users should now have Free plan subscriptions
      
      // If no subscription data at all, they're a new prospect
      if (!hasSubscriptionHistory) {
        return {
          hasSubscription: false,
          isActive: false,
          needsPayment: false,
          isNewProspect: true,
          hasSubscriptionHistory: false
        };
      }
      
      // Has subscription history but no active subscription (lapsed customer)
      return {
        hasSubscription: false,
        isActive: false,
        needsPayment: false,
        isNewProspect: false,
        hasSubscriptionHistory: true,
        plan: response.plan,
        status: response.status
      };
    } catch (error) {
      console.error('Error getting subscription status:', error);
      return {
        hasSubscription: false,
        isActive: false,
        needsPayment: false,
        isNewProspect: true, // Assume new prospect if we can't determine
        hasSubscriptionHistory: false
      };
    }
  }

  /**
   * Create checkout session
   */
  async createCheckoutSession(request: CheckoutRequest): Promise<CheckoutResponse> {
    if (this.useMockData) {
      // Mock checkout session for testing
      console.log('Mock checkout session created for:', request.planId, request.billingCycle, 'discount:', request.discountCode);
      // Simulate Stripe checkout URL
      return { url: `https://checkout.stripe.com/pay/mock-session-${request.planId}-${request.billingCycle}` };
    }

    // Check if user is authenticated
    const user = auth.currentUser;
    if (!user) {
      // User not authenticated, redirect to signup with plan information
      const currentUrl = window.location.pathname + window.location.search;
      const signupUrl = `/signup?redirect=${encodeURIComponent(currentUrl)}&plan=${request.planId}&billing=${request.billingCycle}`;
      window.location.href = signupUrl;
      // Return a promise that never resolves since we're redirecting
      return new Promise(() => {});
    }

    try {
      return await this.makeAuthenticatedRequest<CheckoutResponse>('/checkout', {
        method: 'POST',
        body: JSON.stringify(request),
      });
    } catch (error) {
      console.error('Error creating checkout session:', error);
      throw error;
    }
  }

  /**
   * Create customer portal session
   */
  async createPortalSession(): Promise<PortalResponse> {
    if (this.useMockData) {
      // Mock portal session for testing
      console.log('Mock portal session created');
      return { url: 'https://billing.stripe.com/p/session/mock-portal' };
    }

    try {
      return await this.makeAuthenticatedRequest<PortalResponse>('/portal', {
        method: 'POST',
      });
    } catch (error) {
      console.error('Error creating portal session:', error);
      throw error;
    }
  }

  /**
   * Get usage data
   */
  async getUsageData(): Promise<UsageResponse> {
    if (this.useMockData) {
      // Mock usage data
      const now = new Date();
      const periodStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const periodEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      
      return {
        usage: {
          start: periodStart.toISOString(),
          end: periodEnd.toISOString(),
          chatsUsed: 150,
          teamMembersUsed: 3,
          integrationsUsed: 2,
          storageUsedGB: 1.2,
          locationsUsed: 1
        },
        limits: {
          maxChatsPerMonth: 500,
          maxTeamMembers: 5,
          maxIntegrations: 3,
          maxStorageGB: 10,
          maxLocations: 1
        },
        plan: 'starter-self',
        managementLevel: 'self',
        monthlyUsage: []
      };
    }

    try {
      return await this.makeAuthenticatedRequest<UsageResponse>('/usage');
    } catch (error) {
      console.error('Error getting usage data:', error);
      
      // Return mock data as fallback
      return {
        usage: {
          start: new Date().toISOString(),
          end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          chatsUsed: 45,
          teamMembersUsed: 1,
          integrationsUsed: 0,
          storageUsedGB: 0.5,
          locationsUsed: 1
        },
        limits: {
          maxChatsPerMonth: 500,
          maxTeamMembers: 2,
          maxIntegrations: 2,
          maxStorageGB: 5,
          maxLocations: 1
        },
        plan: 'starter-self',
        managementLevel: 'self',
        monthlyUsage: []
      };
    }
  }

  /**
   * Get subscription data (legacy method for compatibility)
   */
  async getSubscription(): Promise<SubscriptionData | null> {
    try {
      const response = await this.getCurrentSubscription();
      
      if (!response.id) {
        return null;
      }

      return {
        id: response.id,
        merchantId: response.merchantId || '',
        plan: response.plan as any,
        status: response.status as any,
        billingCycle: response.billingCycle as any,
        managementLevel: response.managementLevel as any,
        currentPeriodStart: response.currentPeriodStart ? new Date(response.currentPeriodStart) : new Date(),
        currentPeriodEnd: response.currentPeriodEnd ? new Date(response.currentPeriodEnd) : new Date(),
        trialEnd: response.trialEnd ? new Date(response.trialEnd) : undefined,
        cancelAtPeriodEnd: response.cancelAtPeriodEnd || false,
        createdAt: new Date(),
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error getting subscription:', error);
      return null;
    }
  }

  /**
   * Get usage data (legacy method for compatibility)
   */
  async getUsage(): Promise<UsageData | null> {
    try {
      const response = await this.getUsageData();
      
      return {
        id: 'current',
        merchantId: '',
        subscriptionId: '',
        currentPeriod: {
          start: new Date(response.usage.start),
          end: new Date(response.usage.end),
          chatsUsed: response.usage.chatsUsed,
          teamMembersUsed: response.usage.teamMembersUsed,
          integrationsUsed: response.usage.integrationsUsed,
          storageUsedGB: response.usage.storageUsedGB,
          locationsUsed: response.usage.locationsUsed
        },
        monthlyUsage: response.monthlyUsage.map(month => ({
          month: month.month,
          chatsUsed: month.chatsUsed,
          teamMembersUsed: month.teamMembersUsed,
          integrationsUsed: month.integrationsUsed,
          storageUsedGB: month.storageUsedGB,
          locationsUsed: month.locationsUsed
        })),
        limits: response.limits,
        updatedAt: new Date()
      };
    } catch (error) {
      console.error('Error getting usage:', error);
      return null;
    }
  }

  async updateSubscription(planId: string, billingCycle: 'monthly' | 'yearly'): Promise<void> {
    if (this.useMockData) {
      console.log('Mock subscription update:', planId, billingCycle);
      return;
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/update`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ planId, billingCycle })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to update subscription');
      }
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  }

  async cancelSubscription(cancelAtPeriodEnd: boolean = true): Promise<void> {
    if (this.useMockData) {
      console.log('Mock subscription cancellation:', cancelAtPeriodEnd);
      return;
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/cancel`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ cancelAtPeriodEnd })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }
    } catch (error) {
      console.error('Error canceling subscription:', error);
      throw error;
    }
  }

  async checkUsageLimit(
    limitType: 'chats' | 'teamMembers' | 'integrations' | 'storage' | 'locations',
    requestedAmount: number = 1
  ): Promise<{ allowed: boolean; currentUsage: number; limit: number }> {
    if (this.useMockData) {
      // Mock usage limit check
      const mockLimits = {
        chats: { current: 150, limit: 500 },
        teamMembers: { current: 3, limit: 5 },
        integrations: { current: 2, limit: 3 },
        storage: { current: 1.2, limit: 10 },
        locations: { current: 1, limit: 1 }
      };
      
      const limit = mockLimits[limitType];
      return {
        allowed: limit.current + requestedAmount <= limit.limit,
        currentUsage: limit.current,
        limit: limit.limit
      };
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/check-limit?type=${limitType}&amount=${requestedAmount}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to check usage limit');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error checking usage limit:', error);
      throw error;
    }
  }

  async recordUsageEvent(
    eventType: string,
    eventData: Record<string, any> = {},
    quantity: number = 1
  ): Promise<void> {
    if (this.useMockData) {
      console.log('Mock usage event recorded:', eventType, eventData, quantity);
      return;
    }

    try {
      const token = await this.getAuthToken();
      if (!token) {
        throw new Error('Authentication required');
      }

      const response = await fetch(`${API_BASE_URL}/record-event`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          eventType,
          eventData,
          quantity
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to record usage event');
      }
    } catch (error) {
      console.error('Error recording usage event:', error);
      throw error;
    }
  }
}

export const subscriptionService = new SubscriptionService(); 